import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Badge,
  Divider,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Grid,
  Card,
  CardContent,
  Skeleton,
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { 
  fetchMessageThreads, 
  fetchMessages, 
  sendMessage,
  clearMessages,
  markThreadAsRead 
} from '../../features/messaging/messagingSlice';
import { useFeatureFlag } from '../../config/featureFlags';

const Messaging = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const messagesEndRef = useRef(null);
  
  // Feature flags
  const isMessagingEnabled = useFeatureFlag('FEATURE_PATIENT_MESSAGING');
  const isFilesEnabled = useFeatureFlag('FEATURE_FILES');
  
  // Redux state
  const { threads, messages, sending, sendError } = useSelector((state) => state.messaging);
  const { user } = useSelector((state) => state.auth);
  
  // Local state
  const [selectedThread, setSelectedThread] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    dispatch(fetchMessageThreads());
  }, [dispatch]);

  useEffect(() => {
    if (selectedThread) {
      dispatch(fetchMessages(selectedThread.id));
      dispatch(markThreadAsRead(selectedThread.id));
    }
  }, [selectedThread, dispatch]);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.data]);

  useEffect(() => {
    if (sendError) {
      toast.error(sendError);
    }
  }, [sendError]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedThread) return;

    const messageData = {
      recipient_id: selectedThread.participants.find(p => p.role === 'doctor')?.id,
      text: newMessage.trim(),
      thread_id: selectedThread.id
    };

    try {
      await dispatch(sendMessage(messageData)).unwrap();
      setNewMessage('');
      toast.success('Message sent successfully');
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const filteredThreads = threads.data.filter(thread => {
    const doctorName = thread.participants.find(p => p.role === 'doctor')?.name || '';
    return doctorName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const currentMessages = selectedThread ? messages.data[selectedThread.id] || [] : [];

  const renderThreadItem = (thread) => {
    const doctor = thread.participants.find(p => p.role === 'doctor');
    const isSelected = selectedThread?.id === thread.id;
    
    return (
      <ListItem
        key={thread.id}
        button
        onClick={() => setSelectedThread(thread)}
        selected={isSelected}
        sx={{
          borderRadius: 1,
          mb: 1,
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.light,
            '&:hover': {
              backgroundColor: theme.palette.primary.light,
            },
          },
        }}
      >
        <ListItemAvatar>
          <Badge badgeContent={thread.unread_count} color="primary">
            <Avatar>{doctor?.name?.charAt(0) || 'D'}</Avatar>
          </Badge>
        </ListItemAvatar>
        <ListItemText
          primary={doctor?.name || 'Doctor'}
          secondary={
            <Box>
              <Typography variant="body2" noWrap>
                {thread.last_message?.text || 'No messages yet'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {thread.last_message?.created_at 
                  ? format(new Date(thread.last_message.created_at), 'MMM dd, HH:mm')
                  : ''
                }
              </Typography>
            </Box>
          }
        />
      </ListItem>
    );
  };

  const renderMessage = (message) => {
    const isOwnMessage = message.sender.id === user?.id;
    
    return (
      <Box
        key={message.id}
        sx={{
          display: 'flex',
          justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
          mb: 2,
        }}
      >
        <Box
          sx={{
            maxWidth: '70%',
            backgroundColor: isOwnMessage 
              ? theme.palette.primary.main 
              : theme.palette.grey[100],
            color: isOwnMessage ? 'white' : 'text.primary',
            borderRadius: 2,
            p: 2,
            position: 'relative',
          }}
        >
          <Typography variant="body2">{message.text}</Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              opacity: 0.7, 
              display: 'block', 
              mt: 0.5,
              textAlign: 'right'
            }}
          >
            {format(new Date(message.created_at), 'HH:mm')}
          </Typography>
        </Box>
      </Box>
    );
  };

  if (!isMessagingEnabled) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Messaging feature is currently not available.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" sx={{ p: 2, pb: 1 }}>
        Messages
      </Typography>

      <Grid container sx={{ flex: 1, overflow: 'hidden' }}>
        {/* Threads List */}
        <Grid item xs={12} md={4} sx={{ borderRight: 1, borderColor: 'divider' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              fullWidth
              placeholder="Search doctors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              size="small"
            />
          </Box>

          <Box sx={{ overflow: 'auto', height: 'calc(100vh - 200px)' }}>
            {threads.loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <Box key={index} sx={{ p: 2 }}>
                  <Skeleton variant="rectangular" height={60} />
                </Box>
              ))
            ) : threads.error ? (
              <Alert severity="error" sx={{ m: 2 }}>
                {threads.error}
              </Alert>
            ) : filteredThreads.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No conversations yet
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 1 }}>
                {filteredThreads.map(renderThreadItem)}
              </List>
            )}
          </Box>
        </Grid>

        {/* Chat Area */}
        <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column' }}>
          {selectedThread ? (
            <>
              {/* Chat Header */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
                {isMobile && (
                  <IconButton onClick={() => setSelectedThread(null)} sx={{ mr: 1 }}>
                    <ArrowBackIcon />
                  </IconButton>
                )}
                <Avatar sx={{ mr: 2 }}>
                  {selectedThread.participants.find(p => p.role === 'doctor')?.name?.charAt(0) || 'D'}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {selectedThread.participants.find(p => p.role === 'doctor')?.name || 'Doctor'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Online
                  </Typography>
                </Box>
              </Box>

              {/* Messages */}
              <Box 
                sx={{ 
                  flex: 1, 
                  overflow: 'auto', 
                  p: 2,
                  backgroundColor: theme.palette.grey[50]
                }}
              >
                {messages.loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : messages.error ? (
                  <Alert severity="error">
                    {messages.error}
                  </Alert>
                ) : currentMessages.length === 0 ? (
                  <Box sx={{ textAlign: 'center', p: 3 }}>
                    <Typography color="text.secondary">
                      No messages yet. Start a conversation!
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {currentMessages.map(renderMessage)}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </Box>

              {/* Message Input */}
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {isFilesEnabled && (
                    <IconButton size="small">
                      <AttachFileIcon />
                    </IconButton>
                  )}
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={sending}
                    size="small"
                  />
                  <Button
                    variant="contained"
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sending}
                    sx={{ minWidth: 'auto', px: 2 }}
                  >
                    {sending ? <CircularProgress size={20} /> : <SendIcon />}
                  </Button>
                </Box>
              </Box>
            </>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '100%',
              flexDirection: 'column'
            }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select a conversation to start messaging
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a doctor from the list to begin chatting
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Messaging;
